import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal, Pencil, Pin, PinOff, Trash2 } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface ThreadRowMenuProps {
  token: string;
  /** Namespaces every testid so the lobby card and the sidebar row never collide. */
  testIdPrefix: string;
  title: string;
  onRename: (title: string) => void;
  /** Absent hides the pin item (sidebar room rows have no pin). */
  onPin?: () => void;
  onDelete: () => void;
  /** Overrides the thread wording of the rename dialog title (room rows). */
  renameTitle?: string;
  /** Overrides the thread wording of the delete dialog title (room rows). */
  deleteTitle?: string;
  isPinned?: boolean;
  align?: 'start' | 'end';
  triggerClassName?: string;
}

/**
 * The "..." menu shared by the threads lobby card and the sidebar thread row
 * (Step 5.2). Presentational only: `onRename` fires after the dialog confirms,
 * `onPin` toggles immediately, `onDelete` runs after the user confirms. Every
 * click inside stops propagation, since both hosts nest this inside a row that
 * itself navigates on click.
 *
 * Rename lives in a dialog (not inline) so the input can take focus as soon as
 * the menu closes. Radix would otherwise restore focus to the trigger and the
 * user would have to click the field before typing.
 */
export function ThreadRowMenu(props: ThreadRowMenuProps) {
  const {
    token,
    testIdPrefix,
    title,
    onRename,
    onPin,
    onDelete,
    isPinned = false,
    align = 'end',
    triggerClassName,
    renameTitle,
    deleteTitle,
  } = props

  const { t } = useTranslation('app')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [titleDraft, setTitleDraft] = useState(title)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useLayoutEffect(() => {
    if (!renameOpen) {
      return
    }
    renameInputRef.current?.focus()
    renameInputRef.current?.select()
  }, [renameOpen])

  function openRename() {
    setTitleDraft(title)
    setRenameOpen(true)
  }

  function closeRename() {
    setRenameOpen(false)
    setTitleDraft(title)
  }

  function submitRename() {
    const next = titleDraft.trim()
    if (!next || next === title.trim()) {
      closeRename()
      return
    }
    setRenameOpen(false)
    onRename(next)
  }

  const canSubmit = Boolean(titleDraft.trim()) && titleDraft.trim() !== title.trim()

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('threads.menu.actionsAria')}
            data-testid={`${testIdPrefix}-menu-${token}`}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
              triggerClassName,
            )}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className="w-44"
          onClick={(e) => e.stopPropagation()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {onPin && (
            <DropdownMenuItem
              data-testid={`${testIdPrefix}-menu-pin-${token}`}
              onSelect={() => onPin()}
            >
              {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
              {isPinned ? t('threads.menu.unpin') : t('threads.menu.pin')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            data-testid={`${testIdPrefix}-menu-rename-${token}`}
            onSelect={() => openRename()}
          >
            <Pencil className="h-4 w-4" />
            {t('threads.menu.rename')}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`${testIdPrefix}-menu-delete-${token}`}
            className="text-destructive focus:text-destructive"
            onSelect={() => setConfirmOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
            {t('threads.menu.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={renameOpen}
        onOpenChange={(open) => {
          if (open) {
            openRename()
            return
          }
          closeRename()
        }}
      >
        <DialogContent
          hideClose
          onClick={(e) => e.stopPropagation()}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            renameInputRef.current?.focus()
            renameInputRef.current?.select()
          }}
          data-testid={`${testIdPrefix}-rename-dialog-${token}`}
          className="gap-5 sm:max-w-[400px] rounded-[20px] p-6"
        >
          <DialogHeader>
            <DialogTitle className="text-[17px] font-normal leading-snug">
              {renameTitle ?? t('threads.menu.renameTitle')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('threads.menu.renameDescription')}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              submitRename()
            }}
            className="flex flex-col gap-5"
          >
            <Input
              ref={renameInputRef}
              autoFocus
              autoComplete="off"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation()
                  closeRename()
                }
              }}
              aria-label={t('threads.titleAria')}
              data-testid={`${testIdPrefix}-rename-input-${token}`}
              className="h-10 rounded-md border-foreground/50 bg-transparent"
            />
            <DialogFooter className="flex-row justify-end gap-2 sm:space-x-0">
              <Button
                type="button"
                variant="ghost"
                data-testid={`${testIdPrefix}-rename-cancel-${token}`}
                className="rounded-full"
                onClick={closeRename}
              >
                {t('threads.menu.cancel')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={!canSubmit}
                data-testid={`${testIdPrefix}-rename-confirm-${token}`}
                className="rounded-full bg-muted text-foreground hover:bg-muted/80"
                onClick={submitRename}
              >
                {t('threads.menu.rename')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-delete-dialog-${token}`}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTitle ?? t('threads.menu.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('threads.menu.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={`${testIdPrefix}-delete-cancel-${token}`}
              onClick={() => setConfirmOpen(false)}
            >
              {t('threads.menu.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              data-testid={`${testIdPrefix}-delete-confirm-${token}`}
              onClick={() => {
                setConfirmOpen(false)
                onDelete()
              }}
            >
              {t('threads.menu.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
